import React, { useState } from 'react';
import BaseModal from '../base/BaseModal';
import { Search, Check, Building2, Layers } from 'lucide-react';

const KHU_PHO_LIST = [
  { id: 'all', name: 'Tất cả các khu phố', desc: 'Hiển thị dữ liệu toàn bộ 45 khu phố' },
  ...Array.from({ length: 45 }, (_, i) => {
    const num = String(i + 1).padStart(2, '0');
    return {
      id: `KP ${num}`,
      name: `Khu phố ${num}`,
      desc: `Đơn vị quản lý khu phố ${num}`
    };
  })
];

export default function KhuPhoSelectModal({
  isOpen,
  onClose,
  selectedKhuPho = 'all',
  onSelectKhuPho
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelected, setTempSelected] = useState(selectedKhuPho);

  // Sync tempSelected with prop when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setTempSelected(selectedKhuPho);
      setSearchTerm('');
    }
  }, [isOpen, selectedKhuPho]);

  const filteredList = KHU_PHO_LIST.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleConfirm = () => {
    onSelectKhuPho(tempSelected);
    onClose();
  };

  const footer = (
    <div className="flex items-center justify-end gap-2.5 w-full">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 rounded-md text-xs md:text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
      >
        Hủy
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        className="px-5 py-2 rounded-md text-xs md:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-colors cursor-pointer"
      >
        Xác nhận
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Chọn Khu phố"
      subtitle="Chỉ chọn 01 khu phố hoặc Tất cả để xem báo cáo thống kê"
      size="lg"
      footer={footer}
    >
      <div className="space-y-3 py-1">
        {/* Search Box */}
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Tìm kiếm khu phố (vd: Khu phố 01, KP 21...)"
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md text-xs md:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
        </div>

        {/* Khu Pho Single-Select List */}
        <div className="max-h-[360px] overflow-y-auto space-y-1.5 pr-1">
          {filteredList.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-xs md:text-sm">
              Không tìm thấy khu phố phù hợp
            </div>
          ) : (
            filteredList.map((item) => {
              const isSelected = tempSelected === item.id;
              return (
                <div
                  key={item.id}
                  onClick={() => setTempSelected(item.id)}
                  className={`px-3 py-2.5 rounded-md border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                    isSelected
                      ? 'bg-blue-50/70 border-blue-500 shadow-xs'
                      : 'bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50/60'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {item.id === 'all' ? (
                        <Layers className="w-4 h-4" />
                      ) : (
                        <Building2 className="w-4 h-4" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1 flex flex-col justify-center">
                      <span className={`text-xs md:text-sm font-semibold text-gray-800 leading-snug truncate ${isSelected ? 'text-blue-950 font-bold' : ''}`}>
                        {item.name}
                      </span>
                      {item.id === 'all' && item.desc && (
                        <span className="text-[11px] text-gray-400 leading-snug truncate mt-0.5">
                          {item.desc}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Radio indicator */}
                  <div className="flex-shrink-0">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </BaseModal>
  );
}
